import React, { ReactElement, useEffect, useState } from "react";
import { ExpandableRowsTable } from "./ExpandableRowsTable";
import useAxios from "axios-hooks";
import styles from "./TableStyle.scss";
import processChanges from "./data";
import { translations } from "./translations";
import { NxLoader } from "@nextbank/ui-components";

export interface TableColumn<T> {
  header: string;
  accessorKey?: keyof T;
  cell?: (props: { row: any }) => React.ReactNode;
  className?: string;
}

interface FieldChange {
  previousValue: [];
  newValue: [];
}

interface Changes {
  [key: string]: FieldChange | any;
}

interface ProfileHistoryEntry {
  id: number;
  entryGroup: "UPDATE_CUSTOMER" | "MERGE_CUSTOMER" | "CREATE_CUSTOMER" | "REACTIVATE_CUSTOMER" | "CLOSE_CUSTOMER";
  customerId: number;
  commandId: number;
  changes: Changes;
  executionTime: string;
  approvalTime: string;
  branchName: string;
  initiatorEffectiveName: string;
  approverEffectiveNames: string[] | null;
  commandStatus?: string;
}

const getEntryGroupDisplay = (entryGroup: string): string => {
  const mappings: Record<string, string> = {
    'CREATE_CUSTOMER': translations.createCustomer,
    'MERGE_CUSTOMER': translations.mergeCustomer,
    'UPDATE_CUSTOMER': translations.updateCustomer,
    'REACTIVATE_CUSTOMER': translations.reactivateCustomer,
    'CLOSE_CUSTOMER': translations.closeCustomer
  };
  
  return mappings[entryGroup] || entryGroup;
};

const PAGE_SIZE = 10;

const useProfileHistory = (customerId: string) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [{ data, loading, error }, refetch] = useAxios({
    url: `/customers/${customerId}/history`,
    params: {
      pageNo: currentPage,
      pageSize: PAGE_SIZE
    }
  });

  useEffect(() => {
    if (data?.totalCount) {
      setTotalPages(Math.ceil(data.totalCount / PAGE_SIZE));
    }
  }, [data?.totalCount]);

  return {
    customerData: data,
    loading,
    error,
    currentPage,
    totalPages,
    setCurrentPage,
    refetch
  };
};

export const ProfileHistoryTable: React.FC = (props: any) => {
  const getCustomerIdFromUrl = (): string | undefined => {
    const fullPath = window.location.hash || window.location.pathname;
    const matches = fullPath.match(/(?:#!)?\/customer\/(\d+)\/profile/);

    return matches?.[1];
  };

  const customerId = props?.prop?.id ?? getCustomerIdFromUrl();

  const {
    customerData,
    loading,
    currentPage,
    totalPages,
    setCurrentPage
  } = useProfileHistory(customerId);

  const columns: TableColumn<ProfileHistoryEntry>[] = [
    {
      header: translations.action,
      accessorKey: "entryGroup",
      cell: function renderCell({ row }: any): ReactElement {
        return <span>{getEntryGroupDisplay(row.original.entryGroup)}</span>;
      },
    },
    {
      header: translations.executionDateTime,
      accessorKey: "executionTime",
      cell: function renderCell({ row }: any): ReactElement {
        return <span>{new Date(row.original.executionTime).toLocaleString()}</span>;
      },
    },
    {
      header: translations.initiatingUser,
      accessorKey: "initiatorEffectiveName",
      cell: function renderCell({row}: any): ReactElement {
        const entryGroup = row.original.entryGroup;

        if (entryGroup === "CREATE_CUSTOMER" && row.original.changes?.userId?.newValue) {
          return <span>{row.original.changes.userId.newValue}</span>;
        }

        return <span>{row.original.initiatorEffectiveName}</span>;
      }
    },
    {
      header: translations.approver,
      accessorKey: "approverEffectiveNames",
      cell: function renderCell({ row }: any): ReactElement {
        const approverEffectiveNames = row.original?.approverEffectiveNames;
        const approvalUserIds = row.original?.changes?.approvalUserIds?.newValue;

        const names: string[] = Array.isArray(approverEffectiveNames) && approverEffectiveNames.length > 0
          ? approverEffectiveNames
          : Array.isArray(approvalUserIds) && approvalUserIds.length > 0
            ? approvalUserIds
            : [];

        return (<span> {names.length > 0 ? names.map((name, index) => <div key={index}>{name}</div>)
          : '-'} </span>);
      },
    },
    {
      header: translations.approvalDateTime,
      accessorKey: "approvalTime",
      className: styles["approval"],
      cell: function renderCell({ row }: any): ReactElement {
        return <span>{row.original.approvalTime != null ? new Date(row.original.approvalTime).toLocaleString() : '-'}</span>;
      },
    },
    {
      header: translations.commandStatus,
      accessorKey: "commandStatus",
      cell: function renderCell({ row }: any): ReactElement {
        return (
          <span className={`status-${(row.original.commandStatus || "").toLowerCase()}`}>
            {row.original.commandStatus || translations.notApplicable}
          </span>
        );
      },
    },
  ];

  return (
    <>
      {customerData?.result && !loading ? (
        <ExpandableRowsTable data={processChanges(customerData).result}       columns={columns}
        currentPage={currentPage}
        totalPages={totalPages}
        totalResults={customerData.totalCount}
        onPageChange={setCurrentPage}
        pageSize={PAGE_SIZE} />
      ) : (
        <NxLoader/>
      )}
    </>
  );
};

export default ProfileHistoryTable;
